<script>
import diagnosisService from "@/services/diagnosis";
import debounce from "debounce";

/**
 * @component DiagnosisSelect
 * @description Selector de diagnósticos CIE-10 con búsqueda remota.
 *   Usa remote + remote-method (patrón correcto de Element UI para API-driven search).
 * @example <DiagnosisSelect v-model="diagnosisId" />
 */
export default {
  name: "DiagnosisSelect",
  props: {
    value: { type: String },
    readonly: { type: Boolean, default: false },
    isDisabled: { type: Boolean, default: false },
  },

  data() {
    return {
      diagnoses: [],
      isLoading: false,
    };
  },

  created() {
    this.getDiagnoses({ featured: false, filter: "" });
  },

  watch: {
    /**
     * @description UC-REG-129 — Cuando `value` cambia (típicamente por
     *              propagación cross-section del diagnóstico de la
     *              evolución, UC-REG-127) y el id no está en `diagnoses[]`
     *              local, el `el-select` no encuentra el label y muestra
     *              el id crudo de Mongo. Hacemos fetch del diagnóstico y
     *              lo agregamos al array para que el SELECT muestre la
     *              descripción correcta.
     */
    value: {
      immediate: true,
      async handler(newVal) {
        if (!newVal) return;
        if (this.diagnoses.find((d) => d._id === newVal)) return;
        // Fallback rápido: si está en cache Vuex, usar de ahí sin pegar al API.
        const cached = this.$store.state.cacheDiagnoses?.[newVal];
        if (cached && cached.description) {
          this.diagnoses = [...this.diagnoses, cached];
          return;
        }
        try {
          const fetched = await diagnosisService.getById(newVal);
          if (fetched && fetched._id) {
            this.diagnoses = [...this.diagnoses, fetched];
            if (!this.$store.state.cacheDiagnoses?.[newVal]) {
              this.$store.dispatch("addDiagnoses", fetched);
            }
          }
        } catch (err) {
          // Best-effort: si el fetch falla queda el id visible (peor que el
          // estado anterior pero no rompe el form). Log para Sentry.
          console.warn(
            "DiagnosisSelect: no se pudo hidratar value=" + newVal,
            err
          );
        }
      },
    },
  },

  computed: {
    findDiagnosisById() {
      const diagnosis = this.diagnoses.find((elem) => elem._id === this.value);
      return diagnosis ? diagnosis.description : "Sin datos";
    },
  },

  methods: {
    /**
     * @description Carga diagnósticos desde la API con filtro opcional.
     * @param {Object} diagnosesFilter - { featured, filter }
     */
    async getDiagnoses(diagnosesFilter) {
      this.isLoading = true;
      const result = await diagnosisService.getDiagnoses(diagnosesFilter);
      this.diagnoses = Array.isArray(result) ? result : result.docs || [];
      this.isLoading = false;
    },

    /**
     * @description Búsqueda remota debounced, llamada por remote-method del el-select.
     * @param {string} query - Término de búsqueda ingresado por el usuario
     */
    remoteSearch: debounce(function (query) {
      this.getDiagnoses({ featured: false, filter: query || "" });
    }, 300),

    onInput(value) {
      const selected = this.diagnoses.find((m) => m._id === value);
      if (selected && !this.$store.state.cacheDiagnoses?.[value]) {
        this.$store.dispatch("addDiagnoses", selected);
      }
      this.$emit("input", value);
    },

    /**
     * @description Busca por término CIE-10 y auto-selecciona el primer resultado.
     *              Llamado desde IndicationForm cuando la IA sugiere un diagnóstico.
     * @param {string} term - Término de búsqueda sugerido por la IA (terminología CIE-10)
     */
    async triggerAISearch(term) {
      if (!term) return;
      await this.getDiagnoses({ featured: false, filter: term });
      if (this.diagnoses.length > 0) {
        this.onInput(this.diagnoses[0]._id);
      }
    },
  },
};
</script>

<template lang="pug">
el-select(
  v-if="!readonly"
  :value="value"
  @input="onInput"
  filterable
  remote
  :remote-method="remoteSearch"
  :loading="isLoading"
  :disabled="isDisabled"
  placeholder="Seleccionar diagnóstico"
)
  el-option(
    v-for="item in diagnoses"
    :key="item._id"
    :label="item.description"
    :value="item._id"
  )
    span(style="float: left") {{ item.description }}
    span(v-if="item.featured" style="float: right; color: #8492a6; font-size: 13px") ★

el-input(v-else :value="findDiagnosisById" readonly)
</template>

<style></style>
